import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { SURFACE } from "@/lib/surface";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PlayerHome from "@/pages/PlayerHome";
import Profile from "@/pages/Profile";
import Leaderboard from "@/pages/Leaderboard";
import TVDisplay from "@/pages/TVDisplay";
import TablePublic from "@/pages/TablePublic";
import Arena from "@/pages/Arena";
import PointCounter from "@/pages/PointCounter";
import Rewards from "@/pages/Rewards";
import Community from "@/pages/Community";
import Messages from "@/pages/Messages";
import Wallet from "@/pages/Wallet";
import Verify from "@/pages/Verify";
import Shop from "@/pages/Shop";
import TVCheckin from "@/pages/TVCheckin";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminPlayers from "@/pages/admin/Players";
import AdminMatches from "@/pages/admin/Matches";
import AdminTables from "@/pages/admin/Tables";
import AdminWallet from "@/pages/admin/Wallet";
import AdminLeaderboard from "@/pages/admin/Leaderboard";
import AdminSettings from "@/pages/admin/Settings";
import QRPrint from "@/pages/admin/QRPrint";
import AdminRewards from "@/pages/admin/Rewards";
import AdminVerifications from "@/pages/admin/Verifications";
import AdminArena from "@/pages/admin/Challenges";  // pending_table matches from arena
import AdminItems from "@/pages/admin/Items";
import AdminTVControl from "@/pages/admin/TVControl";

// ---------- Per-surface route trees ----------
function PlayerRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/table/:number" element={<TablePublic />} />
      <Route path="/" element={<ProtectedRoute><PlayerHome /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/players/:id" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/arena" element={<ProtectedRoute><Arena /></ProtectedRoute>} />
      <Route path="/arena/:id" element={<ProtectedRoute><Arena /></ProtectedRoute>} />
      <Route path="/point-counter" element={<ProtectedRoute><PointCounter /></ProtectedRoute>} />
      <Route path="/point-counter/:id" element={<ProtectedRoute><PointCounter /></ProtectedRoute>} />
      <Route path="/rewards" element={<ProtectedRoute><Rewards /></ProtectedRoute>} />
      <Route path="/community" element={<ProtectedRoute><Community /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/chat/dm/:otherId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
      <Route path="/shop" element={<ProtectedRoute><Shop /></ProtectedRoute>} />
      <Route path="/verify" element={<ProtectedRoute><Verify /></ProtectedRoute>} />
      <Route path="/tv/checkin" element={<TVCheckin />} />
      {/* On player surface, /admin and /tv are blocked → bounce home */}
      <Route path="/admin/*" element={<Navigate to="/" replace />} />
      <Route path="/tv" element={<Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AdminRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login adminOnly />} />
      <Route
        path="/admin/qr-print"
        element={
          <ProtectedRoute roles={["admin", "staff"]} loginPath="/login">
            <QRPrint />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute roles={["admin", "staff"]} loginPath="/login">
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="players" element={<AdminPlayers />} />
        <Route path="verifications" element={<AdminVerifications />} />
        <Route path="arena" element={<AdminArena />} />
        <Route path="matches" element={<AdminMatches />} />
        <Route path="matches/new" element={<AdminMatches />} />
        <Route path="tables" element={<AdminTables />} />
        <Route path="wallet" element={<AdminWallet />} />
        <Route path="rewards" element={<AdminRewards />} />
        <Route path="items" element={<AdminItems />} />
        <Route path="tv" element={<AdminTVControl />} />
        <Route path="leaderboard" element={<AdminLeaderboard />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>
      {/* Everything else on admin.* → /admin */}
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}

function TVRoutes() {
  return (
    <Routes>
      <Route path="/tv" element={<TVDisplay />} />
      <Route path="/tv/checkin" element={<TVCheckin />} />
      <Route path="/table/:number" element={<TablePublic />} />
      <Route path="*" element={<Navigate to="/tv" replace />} />
    </Routes>
  );
}

function App() {
  let surfaceRoutes;
  if (SURFACE === "admin") surfaceRoutes = <AdminRoutes />;
  else if (SURFACE === "tv") surfaceRoutes = <TVRoutes />;
  else surfaceRoutes = <PlayerRoutes />;

  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-right" richColors />
        {surfaceRoutes}
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
